import { cloneDeep } from 'lodash';
import { useRef } from 'react';
import { getFramework, getTargetType, useIdentity, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { getPointsBetweenAngle, twoPointToVector } from '@/utils/math-kit';
import { ILayerProps, IPoint } from '@/version-types';
import { CropStagePlugin } from '../controller';
import { useStageCropStore } from '../models/crop-stage-state';
import { getCenterPoint } from '../utils';

export const useRotateOperate = () => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.stage.getActiveOriginIndex();
    const getLayers = () => framework.getLayers(originIndex);
    const getMainLayer = () => framework.getMainLayer(originIndex)!;
    const cropController = framework.getInstance(CropStagePlugin);

    const setIsCropStageOperating = useStageCropStore()((state) => state.setIsCropStageOperating);
    const lastMainLayerRef = useRef<ILayerProps | null>(null);
    const lastLayersRef = useRef<ILayerProps[] | null>(null);
    const centerPointRef = useRef<IPoint | null>(null);
    const lastPointRef = useRef<IPoint>({ x: 0, y: 0 });

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-type');

        if (!getTargetType(target)) return;

        if (dataType !== 'rotate') return;

        if (e.isStart) {
            const mainLayer = getMainLayer();
            const layers = getLayers();
            lastMainLayerRef.current = cloneDeep(mainLayer);
            lastLayersRef.current = cloneDeep(layers);
            lastPointRef.current = { x: e.startPoint.x, y: e.startPoint.y };
            setIsCropStageOperating(true);
            const parentElement = target.parentElement;
            if (!parentElement) return;

            if (!centerPointRef.current) {
                centerPointRef.current = getCenterPoint(parentElement);
            }
            return;
        }

        if (!lastMainLayerRef.current || !lastLayersRef.current || !centerPointRef.current) {
            return;
        }

        const currentPoint = { x: e.currentPoint.x, y: e.currentPoint.y };

        // 获取从初始点的旋转角度
        let currentRotate = getPointsBetweenAngle(
            twoPointToVector(centerPointRef.current, lastPointRef.current),
            twoPointToVector(centerPointRef.current, currentPoint),
        );

        if (currentRotate < 0) {
            currentRotate = 360 + currentRotate;
        }

        cropController.rotateMainImageOperate(currentRotate, lastMainLayerRef.current, lastLayersRef.current);

        if (e.isEnd) {
            setIsCropStageOperating(false);
            lastMainLayerRef.current = null;
            lastLayersRef.current = null;
        }
    });
};
